import type {
  CompanionMessageRow,
  CompanionRoomFileRow,
  EnterCompanionRoomResult,
  GetCompanionRoomFilesParameter,
  GetCompanionRoomFilesResult,
  GetCompanionRoomInfoParameter,
  GetCompanionRoomInfoResult,
  GetLatestCompanionMessagesParameter,
  GetLatestCompanionMessagesResult,
  GetMyMessagesWithRepliesParameter,
  GetMyMessagesWithRepliesResult,
  GetOlderCompanionMessagesParameter,
  GetOlderCompanionMessagesResult,
  GetTopPinnedCompanionMessagesParameter,
  GetTopPinnedCompanionMessagesResult,
  MyMessageThreadRow,
  PullNewCompanionMessagesResult,
  SendCompanionMessageParameter,
  SendCompanionMessageResult,
} from "./CompanionRoomType";

const iso = (offsetMin: number): string =>
  new Date(Date.now() + offsetMin * 60_000).toISOString();

const baseMessages: CompanionMessageRow[] = [
  {
    id: "10001",
    chatRoomId: "cr_mock",
    messageType: "TEXT",
    content: "大家好，欢迎进入聊天室。",
    fromUser: false,
    senderName: "王老师",
    senderSpaceRole: "teacher",
    visibility: "PUBLIC",
    moderationStatus: "APPROVED",
    shielded: false,
    deleted: false,
    sentAt: iso(-120),
  },
  {
    id: "10002",
    chatRoomId: "cr_mock",
    messageType: "TEXT",
    content: "老师好，请问今天怎么看？",
    fromUser: true,
    senderName: "我",
    visibility: "PUBLIC",
    moderationStatus: "APPROVED",
    shielded: false,
    deleted: false,
    sentAt: iso(-119),
  },
  {
    id: "10003",
    chatRoomId: "cr_mock",
    messageType: "TEXT",
    content: "私信回复您：注意仓位。",
    fromUser: false,
    senderName: "王老师",
    senderSpaceRole: "teacher",
    visibility: "PRIVATE",
    moderationStatus: "APPROVED",
    replyToMessageId: "10002",
    shielded: false,
    deleted: false,
    sentAt: iso(-118),
  },
];

let mockMaxId = 10003;
const mockPendingTail: CompanionMessageRow[] = [];

export function mockGetCompanionRoomInfo(
  _p: GetCompanionRoomInfoParameter
): GetCompanionRoomInfoResult {
  return {
    enabled: true,
    chatRoomId: "cr_mock",
    roomTitle: "演示聊天室",
    chatOpen: true,
    maxPerMinute: 10,
    maxPerDay: 200,
    remainPerDay: 180,
    maxImagePerDay: 1,
    remainImagePerDay: 1,
    maxDisplayCount: 200,
  };
}

export function mockEnterCompanionRoom(_p: {
  chatRoomId: string;
}): EnterCompanionRoomResult {
  return { chatRoomId: _p.chatRoomId, enteredAt: new Date().toISOString() };
}

export function mockGetLatestCompanionMessages(
  p: GetLatestCompanionMessagesParameter
): GetLatestCompanionMessagesResult {
  const merged = [...baseMessages, ...mockPendingTail].sort((a, b) =>
    a.id > b.id ? 1 : -1
  );
  void p.chatRoomId;
  const size = Math.min(p.size ?? 50, merged.length);
  const tail = merged.slice(-size);
  return {
    list: tail,
    hasOlder: tail.length > 0 && tail[0].id !== merged[0]?.id,
    maxApprovedId: merged.at(-1)?.id ?? null,
    maxRejectedId: null,
  };
}

export function mockGetOlderCompanionMessages(
  p: GetOlderCompanionMessagesParameter
): GetOlderCompanionMessagesResult {
  const merged = [...baseMessages, ...mockPendingTail].sort((a, b) =>
    a.id > b.id ? 1 : -1
  );
  const idx = merged.findIndex((m) => m.id === p.anchorMessageId);
  const older = idx > 0 ? merged.slice(Math.max(0, idx - (p.size ?? 20)), idx) : [];
  return {
    list: older,
    hasMore: older.length > 0 && older[0].id !== merged[0]?.id,
  };
}

export function mockPullNewCompanionMessages(
  p: { maxApprovedOrSelfMessageId?: string | null; maxApprovedId?: string | null }
): PullNewCompanionMessagesResult {
  const merged = [...baseMessages, ...mockPendingTail].sort((a, b) =>
    a.id > b.id ? 1 : -1
  );
  const minEx =
    (p.maxApprovedOrSelfMessageId?.trim() || p.maxApprovedId?.trim() || "") ?? "";
  const next = minEx
    ? merged.filter((m) => m.id.localeCompare(minEx, undefined, { numeric: true }) > 0)
    : merged;
  return {
    list: next,
    hasMore: false,
    maxApprovedId: merged.at(-1)?.id ?? null,
    maxRejectedId: null,
    rejectedEvents: [],
    maxPerDay: 200,
    remainPerDay: 180,
    maxImagePerDay: 1,
    remainImagePerDay: 1,
  };
}

export function mockSendCompanionMessage(
  p: SendCompanionMessageParameter
): SendCompanionMessageResult {
  mockMaxId += 1;
  const id = String(mockMaxId);
  const msg: CompanionMessageRow = {
    id,
    chatRoomId: p.chatRoomId,
    messageType: p.messageType,
    content: p.content ?? "",
    fileUrl: p.fileUrl ?? undefined,
    fileName: p.fileName ?? undefined,
    fromUser: true,
    senderName: "我",
    visibility: p.visibility,
    moderationStatus: "PENDING",
    replyToMessageId: p.replyToMessageId ?? undefined,
    shielded: false,
    deleted: false,
    sentAt: new Date().toISOString(),
  };
  mockPendingTail.push(msg);
  return {
    message: msg,
    maxPerMinute: 10,
    maxPerDay: 200,
    remainPerMinute: 9,
    remainPerDay: 179,
    maxImagePerDay: 1,
    remainImagePerDay: p.messageType === "IMAGE" ? 0 : 1,
  };
}

function mockThread(): MyMessageThreadRow {
  const my: CompanionMessageRow = {
    id: "10002",
    chatRoomId: "cr_mock",
    messageType: "TEXT",
    content: "老师好，请问今天怎么看？",
    fromUser: true,
    senderName: "我",
    visibility: "PUBLIC",
    sentAt: iso(-119),
  };
  const r1: CompanionMessageRow = {
    id: "10003",
    chatRoomId: "cr_mock",
    messageType: "TEXT",
    content: "公开回复：关注成交量。",
    fromUser: false,
    senderName: "王老师",
    senderSpaceRole: "teacher",
    visibility: "PUBLIC",
    replyToMessageId: "10002",
    sentAt: iso(-118),
  };
  const r2: CompanionMessageRow = {
    id: "10004",
    chatRoomId: "cr_mock",
    messageType: "TEXT",
    content: "私信补充：别追高。",
    fromUser: false,
    senderName: "小李",
    senderSpaceRole: "assistant",
    visibility: "PRIVATE",
    replyToMessageId: "10002",
    sentAt: iso(-117),
  };
  return {
    myMessage: my,
    replies: [r1, r2],
    replyCount: 2,
  };
}

export function mockGetMyMessagesWithReplies(
  _p: GetMyMessagesWithRepliesParameter
): GetMyMessagesWithRepliesResult {
  return {
    list: [mockThread()],
    hasMore: false,
    nextCursorSentAt: null,
    nextCursorMessageId: null,
  };
}

export function mockGetCompanionRoomFiles(
  _p: GetCompanionRoomFilesParameter
): GetCompanionRoomFilesResult {
  const row: CompanionRoomFileRow = {
    messageId: "20001",
    fileUrl: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
    fileName: "示例资料.pdf",
    messageType: "PDF",
    senderName: "王老师",
    sentAt: iso(-60),
  };
  return {
    list: [row],
    hasMore: false,
    nextCursorSentAt: null,
    nextCursorMessageId: null,
  };
}

export function mockGetTopPinnedMessages(
  _p: GetTopPinnedCompanionMessagesParameter
): GetTopPinnedCompanionMessagesResult {
  return {
    list: [
      {
        id: "10001",
        chatRoomId: "cr_mock",
        messageType: "TEXT",
        content: "置顶：今日风险提示。",
        fromUser: false,
        senderName: "王老师",
        senderSpaceRole: "teacher",
        visibility: "PUBLIC",
        sentAt: iso(-180),
      },
    ],
  };
}
